import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, GlassWater, ShoppingBag, Boxes, LogOut,
  Upload, FolderTree, Layers, Menu, X, Award, Package, MapPin,
} from 'lucide-react';

import { FAB } from '@/components/FAB';
import { InstallPrompt } from '@/components/InstallPrompt';
import { UpdatePrompt } from '@/components/UpdatePrompt';
import { useAuthStore } from '@/features/auth/authStore';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';

const items = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/wines', label: 'Vinos', icon: GlassWater },
  { to: '/tastings', label: 'Catas', icon: Award },
  { to: '/verticals', label: 'Verticales', icon: Layers },
  { to: '/purchases', label: 'Compras', icon: ShoppingBag },
  { to: '/inventory', label: 'Inventario', icon: Boxes },
  { to: '/boxes', label: 'Cajas', icon: Package },
  { to: '/locations', label: 'Lugares', icon: MapPin },
  { to: '/import', label: 'Importar', icon: Upload },
  { to: '/catalog', label: 'Catálogos', icon: FolderTree },
];

export function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const reset = useAuthStore((s) => s.reset);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Cerrar el menú mobile al navegar.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Bloquear scroll del body cuando el drawer está abierto.
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch { /* silenciar */ }
    reset();
    navigate('/login');
  };

  const initials = (user?.name ?? 'U')
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const currentLabel =
    items.find((i) => (i.end ? location.pathname === i.to : location.pathname.startsWith(i.to)))
      ?.label ?? 'Notas de Cata';

  return (
    <div className="min-h-screen flex bg-white">
      {/* Topbar mobile */}
      <header className="md:hidden fixed top-0 inset-x-0 z-30 bg-white border-b border-notion-200 h-12 flex items-center px-2 gap-2">
        <button
          type="button"
          aria-label="Abrir menú"
          onClick={() => setMobileOpen(true)}
          className="p-2 rounded hover:bg-notion-100"
        >
          <Menu size={18} />
        </button>
        <div className="flex items-center gap-2 min-w-0">
          <img src={`${import.meta.env.BASE_URL}favicon.svg`} alt="" className="w-6 h-6 shrink-0" aria-hidden="true" />
          <span className="text-sm font-medium text-notion-800 truncate">{currentLabel}</span>
        </div>
      </header>

      {/* Backdrop mobile */}
      {mobileOpen && (
        <button
          type="button"
          aria-label="Cerrar menú"
          onClick={() => setMobileOpen(false)}
          className="md:hidden fixed inset-0 z-40 bg-notion-900/30"
        />
      )}

      {/* Sidebar (desktop estático, mobile drawer) */}
      <aside
        className={cn(
          'sidebar w-64 md:w-60 flex-col z-50',
          'md:flex',
          mobileOpen
            ? 'flex fixed inset-y-0 left-0 transition-transform'
            : 'hidden md:flex',
        )}
      >
        <div className="px-2 py-3 border-b border-notion-200 flex items-center justify-between">
          <div className="flex items-center gap-2 px-2 py-1 min-w-0">
            <img src={`${import.meta.env.BASE_URL}favicon.svg`} alt="" className="w-7 h-7 shrink-0" aria-hidden="true" />
            <div className="leading-tight min-w-0">
              <p
                className="text-[13px] font-medium text-notion-800 tracking-tight"
                style={{ fontFamily: "'JetBrains Mono', 'SF Mono', Menlo, monospace" }}
              >
                notas_de_cata
              </p>
              <p className="text-[11px] text-notion-500 truncate max-w-[140px]">
                {user?.tenant?.name ?? 'Mi cava'}
              </p>
            </div>
          </div>
          <button
            type="button"
            aria-label="Cerrar menú"
            onClick={() => setMobileOpen(false)}
            className="md:hidden p-1.5 rounded hover:bg-notion-200"
          >
            <X size={16} />
          </button>
        </div>

        <nav className="flex-1 px-2 py-2 overflow-y-auto">
          <p className="sidebar-section-label">Workspace</p>
          {items.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cn('sidebar-item', isActive && 'active')
              }
            >
              <Icon size={15} className="text-notion-500" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="px-3 py-3 border-t border-notion-200">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-full bg-notion-200 text-notion-700 flex items-center justify-center text-[11px] font-medium">
              {initials}
            </div>
            <div className="text-[12px] text-notion-600 truncate" title={user?.email}>
              {user?.email}
            </div>
          </div>
          <button onClick={logout} className="btn-ghost w-full justify-start text-[13px]">
            <LogOut size={14} /> Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Main content (con padding-top en mobile para el topbar) */}
      <main className="flex-1 overflow-y-auto pt-12 md:pt-0">
        <Outlet />
      </main>

      {/* FAB "Probar vino" */}
      <FAB />

      {/* PWA: invitación a instalar + aviso de update */}
      <InstallPrompt />
      <UpdatePrompt />
    </div>
  );
}
